<template>
  <div class="requisite-block">
    <base-loader v-if="loading" />

    <div
      class="requisite-item"
      v-else-if="!loading && requisiteTypes.length > 0"
      v-for="requisiteType in requisiteTypes"
      :key="requisiteType.id"
    >
      <div class="item-header">
        <h3 class="header-title">{{ requisiteType.name }}</h3>
        <span v-if="requisiteType.description" class="header-desc">
          {{ requisiteType.description }}
        </span>
      </div>

      <div
        v-for="field in requisiteType.schema?.fields || []"
        :key="field.code"
        class="form-group"
      >
        <base-label
          class="form-label"
          :for="`${requisiteType.code}-${field.code}`"
        >
          {{ field.label }}:
        </base-label>

        <!-- Используем textarea, если тип поля — text-->
        <base-textarea
          class="form-textarea"
          v-if="field.type === 'text'"
          :id="`${requisiteType.code}-${field.code}`"
          v-model="formData[requisiteType.code][field.code]"
        ></base-textarea>

        <!-- Выпадающий список -->
        <select
          class="form-select"
          v-else-if="field.type === 'select'"
          :id="`${requisiteType.code}-${field.code}`"
          v-model="formData[requisiteType.code][field.code]"
        >
          <option disabled value>Выберите значение</option>
          <option
            v-for="option in field.options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <!-- Числа -->
        <base-input
          class="form-input"
          v-else-if="field.type === 'number'"
          :id="`${requisiteType.code}-${field.code}`"
          v-model="formData[requisiteType.code][field.code]"
          :type="field.type"
        />

        <!-- Дата и текст -->
        <base-input
          class="form-input"
          v-else
          :id="`${requisiteType.code}-${field.code}`"
          v-model="formData[requisiteType.code][field.code]"
          :type="field.type === 'date' ? 'date' : 'text'"
        />
      </div>

      <AttachmentUploader
        v-if="requisiteType.allow_attachments"
        v-model:attachment-ids="attachments[requisiteType.code]"
        :requisite-type-id="requisiteType.id"
        :requisite-code="requisiteType.code"
        :form-id="formId"
        :existing-attachments="getExistingAttachments(requisiteType)"
      />
    </div>

    <div v-else-if="requisiteTypes.length === 0" class="no-data ">
      Пользовательские поля отсутствуют
    </div>
  </div>
</template>

<script>
import AttachmentUploader from "@/components/Attachment/Uploader.vue";

import { useRequisiteStore } from "@/stores/requisiteStore";

export default {
  name: "RequisiteCreate",
  components: { AttachmentUploader },

  props: {
    allowedModels: Array,
    requisitableType: String,
    requisitableId: {
      type: [Number, String],
      default: null,
    },
    formId: {
      type: String,
      default: "",
    },
  },

  emits: ["requisites-updated"],

  data() {
    return {
      loading: false,
      requisiteTypes: [],
      formData: {},
      attachments: {},
    };
  },

  computed: {
    requisiteStore() {
      return useRequisiteStore();
    },
  },

  watch: {
    $data: {
      handler() {
        this.$emit("requisites-updated", {
          formData: this.formData,
          attachments: this.attachments,
        });
      },
      deep: true,
    },
  },

  methods: {
    async loadRequisites() {
      this.loading = true;
      try {
        if (this.requisitableId) {
          await this.requisiteStore.loadRequisites({
            id: this.requisitableId,
            allowed_models: this.allowedModels,
            requisitable_type: this.requisitableType,
          });
          this.requisiteTypes = this.requisiteStore.requisiteTypes;
        } else {
          this.requisiteTypes =
            await this.requisiteStore.loadRequisiteTypes(
              this.allowedModels
            );
        }

        this.initializeData();
      } catch (error) {
        console.error("Ошибка:", error);
      } finally {
        this.loading = false;
      }
    },

    initializeData() {
      this.requisiteTypes.forEach((type) => {
        if (!this.formData[type.code]) {
          this.formData[type.code] = {};
        }

        if (!this.attachments[type.code]) {
          this.attachments[type.code] = this.getExistingAttachmentIds(
            type
          );
        }

        if (type.schema?.fields) {
          type.schema.fields.forEach((field) => {
            if (this.formData[type.code][field.code] === undefined) {
              this.formData[type.code][field.code] =
                this.getExistingFieldValue(type, field.code);
            }
          });
        }
      });
    },

    getExistingRequisite(type) {
      if (!this.requisitableId) return null;

      return this.requisiteStore.requisites.find(
        (requisite) => requisite.requisite_type?.id === type.id
      );
    },

    getExistingFieldValue(type, fieldCode) {
      const requisite = this.getExistingRequisite(type);
      const value = requisite?.data?.[fieldCode];

      return value === undefined || value === null ? "" : value;
    },

    getExistingAttachments(type) {
      if (!this.requisitableId) return [];

      return this.requisiteStore.getAttachments(type.id);
    },

    getExistingAttachmentIds(type) {
      if (!this.requisitableId) return [];

      return [...(this.requisiteStore.attachmentIds[type.id] || [])];
    },

    async createRequisites(id) {
      const requisitableId = id || this.requisitableId;

      if (this.requisitableId) {
        await Promise.all(
          this.requisiteTypes
            .filter((type) => this.shouldSaveRequisite(type))
            .map((type) => {
              this.requisiteStore.attachmentIds[type.id] = [
                ...(this.attachments[type.code] || []),
              ];

              return this.requisiteStore.saveRequisite(
                type,
                { ...(this.formData[type.code] || {}) },
                requisitableId,
                this.requisitableType
              );
            })
        );

        return;
      }

      const requisitesToCreate = this.requisiteStore.getCreatePayload(
        this.requisitableType,
        this.formData,
        this.attachments,
        requisitableId
      );

      if (requisitesToCreate.length > 0) {
        await this.requisiteStore.createRequisites(
          requisitesToCreate
        );
      }
    },

    shouldSaveRequisite(type) {
      const hasExistingRequisite = Boolean(
        this.getExistingRequisite(type)
      );
      const data = this.formData[type.code] || {};
      const hasValues = Object.values(data).some(
        (value) => value !== "" && value !== undefined && value !== null
      );
      const hasAttachments = this.attachments[type.code]?.length > 0;

      return hasExistingRequisite || hasValues || hasAttachments;
    },
  },

  async created() {
    await this.loadRequisites();
  },
};
</script>

<style scoped>
.requisite-item {
  margin-bottom: var(--base-out-indent);
}

.requisite-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.item-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.header-title {
  margin-bottom: 7px;
}

.header-desc {
  color: #666;
}

.no-data {
  margin-bottom: var(--base-out-indent);
}
</style>
